/**
 * ChatActions - Phase B Mission B2
 *
 * Inline action buttons rendered within chat messages.
 * When Noesis mentions a mission, spec, or deployment, it can show
 * inline actions that trigger UICommands.
 *
 * Action types:
 * - view_mission: Open mission view in center panel
 * - open_deployment: Open deployment panel
 * - open_convops: Open ConvOps console
 * - toggle_drawer: Toggle the drawer
 */

import React from 'react';
import { Eye, Rocket, FileText, PanelBottomClose } from 'lucide-react';
import { applyStudioUICommand, type StudioUICommand } from './uiCommands.js';

/**
 * Structured action metadata that can be attached to chat messages
 */
export interface ChatAction {
    type: 'view_mission' | 'open_deployment' | 'open_convops' | 'toggle_drawer';
    label?: string;
    missionId?: string;
    specId?: string;
}

/**
 * Map a ChatAction to a StudioUICommand
 */
function actionToCommand(action: ChatAction): StudioUICommand | null {
    switch (action.type) {
        case 'view_mission':
            return { type: 'set_center_app', appKey: 'missions' };
        case 'open_deployment':
            return { type: 'set_center_app', appKey: 'deployment' };
        case 'open_convops':
            return { type: 'set_center_app', appKey: 'convops' };
        case 'toggle_drawer':
            return { type: 'toggle_drawer' };
        default:
            return null;
    }
}

/**
 * Get icon for action type
 */
function getActionIcon(type: ChatAction['type']): React.ReactNode {
    switch (type) {
        case 'view_mission':
            return <Eye className="w-3.5 h-3.5" />;
        case 'open_deployment':
            return <Rocket className="w-3.5 h-3.5" />;
        case 'open_convops':
            return <FileText className="w-3.5 h-3.5" />;
        case 'toggle_drawer':
            return <PanelBottomClose className="w-3.5 h-3.5" />;
        default:
            return null;
    }
}

/**
 * Get default label for action type
 */
function getActionLabel(action: ChatAction): string {
    if (action.label) return action.label;
    switch (action.type) {
        case 'view_mission':
            return 'View Mission';
        case 'open_deployment':
            return 'Open Deployment';
        case 'open_convops':
            return 'Open ConvOps';
        case 'toggle_drawer':
            return 'Toggle Drawer';
        default:
            return 'Action';
    }
}

interface ChatActionsProps {
    actions: ChatAction[];
}

/**
 * Render inline action buttons for a chat message
 */
export const ChatActions: React.FC<ChatActionsProps> = ({ actions }) => {
    if (!actions || actions.length === 0) return null;

    const handleAction = (action: ChatAction) => {
        const cmd = actionToCommand(action);
        if (cmd) {
            console.log('[ChatActions] Executing action:', action, '-> command:', cmd);
            applyStudioUICommand(cmd);
        }
    };

    return (
        <div className="flex flex-wrap gap-1.5 mt-2">
            {actions.map((action, index) => (
                <button
                    key={`${action.type}-${index}`}
                    onClick={() => handleAction(action)}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-cyan-300 hover:text-white bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 hover:border-cyan-500/50 rounded-md transition-colors"
                >
                    {getActionIcon(action.type)}
                    <span>{getActionLabel(action)}</span>
                </button>
            ))}
        </div>
    );
};

/**
 * Parse actions from a message content (for pluginCard type messages)
 * This extracts structured actions from JSON content
 */
export function parseActionsFromContent(content: string): ChatAction[] {
    try {
        const parsed = JSON.parse(content);
        if (parsed.actions && Array.isArray(parsed.actions)) {
            return parsed.actions.filter(
                (a: unknown): a is ChatAction =>
                    typeof a === 'object' &&
                    a !== null &&
                    'type' in a &&
                    typeof (a as ChatAction).type === 'string'
            );
        }
    } catch {
        // Not JSON or no actions
    }
    return [];
}

export default ChatActions;
